// Side drawer for tenant bug submission.
//
// Opens from the Bug icon in the top nav. Captures:
//
//   - title (required, 3-200 chars)
//   - description (required, 3-5000 chars)
//   - severity (low | medium | high | critical, default medium)
//   - category (UI | API | Performance | Data | Other, default Other)
//
// Auto-attached context (collapsible, editable-as-a-block via the
// "Include browser context" switch):
//
//   - current page URL
//   - browser User-Agent
//   - viewport (w × h)
//   - last 10 captured console errors (via consoleErrorBuffer.js)
//
// On submit: POST /api/user/v1/bug-reports → snackbar with the new
// report id → drawer closes.
import React, { useState, useMemo } from "react";
import {
  Box, Stack, Typography, IconButton, Select,
  MenuItem, InputLabel, Button,
  Collapse, Alert as MuiAlert, CircularProgress, Modal, Slide,
} from "@mui/material";
import { Bug, ChevronDown, ChevronUp } from "lucide-react";

import bugReports from "../../utils/axios/v1/bugReportsAxios";
import { getRecentConsoleErrors } from "../../utils/consoleErrorBuffer";
import colors from "../../utils/colors";
import LayerdIcon from "./LayerdIcon";
import { UI_ICONS } from "../../icons/uiLucideIcons";
import { GreenTextField, GreenSelectForm, GreenSwitch } from "./Greens";
import { CancelButton, PrimaryButton } from "./Button";
import { validateBugReportForm, getCharPolicyFieldText } from "../../utils/validation";

const brandBlue = colors.primary[300];

/** Matches GreenSelectForm outlined control height (label + value row). */
const selectControlHeight = 56;

const selectControlBorderSx = {
  border: `1px solid ${colors.neutral[300]}`,
  borderRadius: "8px",
  backgroundColor: "#fff",
};

const selectControlRowSx = {
  minHeight: selectControlHeight,
  height: selectControlHeight,
  px: 2,
  boxSizing: "border-box",
};

const descriptionHelperSx = {
  "& .MuiFormHelperText-root": {
    mx: 0,
    mt: 0.75,
    fontSize: 12,
    color: colors.neutral[600],
    textAlign: "right",
  },
};

const descriptionFieldSx = {
  ...descriptionHelperSx,
  "& .MuiOutlinedInput-root.MuiInputBase-multiline": {
    padding: 0,
  },
};

const SEVERITY_OPTIONS = [
  { value: "low", label: "Low", color: colors.neutral[500] },
  { value: "medium", label: "Medium", color: brandBlue },
  { value: "high", label: "High", color: colors.yellow[200] },
  { value: "critical", label: "Critical", color: colors.red[100] },
];

const CATEGORY_OPTIONS = ["UI", "API", "Performance", "Data", "Other"];

const menuItemSx = {
  fontSize: 14,
  color: colors.neutral[1000],
};

const SeverityOptionRow = ({ color, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
    <Box component="span" sx={{ fontSize: 14, lineHeight: "20px" }}>{label}</Box>
  </Box>
);

export default function ReportBugDrawer({ open, onClose, onSubmitted }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState("medium");
  const [category, setCategory] = useState("Other");
  const [includeContext, setIncludeContext] = useState(true);
  const [contextOpen, setContextOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const validation = useMemo(
    () => validateBugReportForm({ title, description }),
    [title, description],
  );

  const showFieldErr = (field) => (submitAttempted || touched[field]) && validation.errors[field];

  const context = useMemo(() => {
    if (typeof window === "undefined") return {};
    return {
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      console_errors: getRecentConsoleErrors(),
    };
  }, [open]);

  const reset = () => {
    setTitle(""); setDescription("");
    setSeverity("medium"); setCategory("Other");
    setIncludeContext(true); setContextOpen(false);
    setError(null);
    setTouched({});
    setSubmitAttempted(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose && onClose();
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    const result = validateBugReportForm({ title, description });
    if (!result.valid) return;

    setError(null);
    setSubmitting(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim(),
        severity,
        category,
      };
      if (includeContext) {
        body.page_url = context.page_url || undefined;
        body.user_agent = context.user_agent || undefined;
        body.viewport = context.viewport || undefined;
        body.console_errors = context.console_errors || [];
      }
      const r = await bugReports.create(body);
      onSubmitted && onSubmitted(r.data);
      reset();
      onClose && onClose();
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = validation.valid;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      BackdropProps={{ sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" } }}
    >
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: { xs: "100%", sm: 480 },
            maxHeight: "100vh",
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1300,
            borderTopLeftRadius: "8px",
            outline: "none",
            border: "none",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header */}
          <Box sx={{ px: 3, pt: 3, pb: 2.5, borderBottom: `1px solid ${colors.neutral[200]}`, flexShrink: 0 }}>
            <Stack direction="row" alignItems="flex-start" spacing={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  backgroundColor: colors.primary[100],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  mt: 0.25,
                }}
              >
                <LayerdIcon icon={Bug} size={20} color="primary" />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                  <Typography sx={{ fontSize: 18, fontWeight: "medium", lineHeight: "24px", color: colors.neutral[1000] }}>
                    Report a bug
                  </Typography>
                  <IconButton
                    onClick={handleClose}
                    disabled={submitting}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "8px",
                      mt: -0.5,
                      flexShrink: 0,
                      "&:hover": { backgroundColor: colors.neutral[50] },
                    }}
                  >
                    <LayerdIcon icon={UI_ICONS.close} size={18} color="neutral" />
                  </IconButton>
                </Stack>
                <Typography sx={{ fontSize: 13, lineHeight: "20px", color: colors.neutral[700], mt: 0.75, pr: 1 }}>
                  Tell us what went wrong. Our team will follow up by email if we need more details.
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Body */}
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            <Stack spacing={3} sx={{ px: 3, pt: 3, pb: 2 }}>
              <GreenTextField
                fullWidth
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => setTouched((prev) => ({ ...prev, title: true }))}
                error={Boolean(showFieldErr("title"))}
                helperText={showFieldErr("title") || undefined}
                inputProps={{ maxLength: 200 }}
                disabled={submitting}
                required
              />

              <GreenTextField
                fullWidth
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={() => setTouched((prev) => ({ ...prev, description: true }))}
                error={Boolean(showFieldErr("description"))}
                helperText={getCharPolicyFieldText("plainText", showFieldErr("description"))}
                inputProps={{ maxLength: 5000 }}
                multiline
                minRows={5}
                disabled={submitting}
                required
                sx={descriptionFieldSx}
              />

              <Stack direction="row" spacing={2} sx={{ width: "100%", alignItems: "flex-start" }}>
                <GreenSelectForm variant="outlined" fullWidth disabled={submitting} sx={{ flex: 1, minWidth: 0 }}>
                  <InputLabel id="bug-severity-label">Severity</InputLabel>
                  <Select
                    labelId="bug-severity-label"
                    label="Severity"
                    value={severity}
                    onChange={(e) => setSeverity(e.target.value)}
                    fullWidth
                    renderValue={(value) => {
                      const option = SEVERITY_OPTIONS.find((s) => s.value === value);
                      return option
                        ? <SeverityOptionRow color={option.color} label={option.label} />
                        : value;
                    }}
                  >
                    {SEVERITY_OPTIONS.map((s) => (
                      <MenuItem key={s.value} value={s.value} sx={menuItemSx}>
                        <SeverityOptionRow color={s.color} label={s.label} />
                      </MenuItem>
                    ))}
                  </Select>
                </GreenSelectForm>

                <GreenSelectForm variant="outlined" fullWidth disabled={submitting} sx={{ flex: 1, minWidth: 0 }}>
                  <InputLabel id="bug-category-label">Category</InputLabel>
                  <Select
                    labelId="bug-category-label"
                    label="Category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    fullWidth
                  >
                    {CATEGORY_OPTIONS.map((c) => (
                      <MenuItem key={c} value={c} sx={menuItemSx}>{c}</MenuItem>
                    ))}
                  </Select>
                </GreenSelectForm>
              </Stack>

              <Box sx={{ ...selectControlBorderSx, overflow: "hidden" }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={2}
                  sx={selectControlRowSx}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
                    <GreenSwitch
                      checked={includeContext}
                      onChange={(_, v) => {
                        setIncludeContext(v);
                        if (!v) setContextOpen(false);
                      }}
                      disabled={submitting}
                      sx={{ flexShrink: 0, ml: -0.5 }}
                    />
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: colors.neutral[1000], lineHeight: "20px" }}>
                      Include browser context
                    </Typography>
                  </Stack>
                  {includeContext && (
                    <Button
                      size="small"
                      onClick={() => setContextOpen(!contextOpen)}
                      endIcon={contextOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      sx={{
                        textTransform: "none",
                        fontSize: 12,
                        fontWeight: 500,
                        color: colors.neutral[700],
                        flexShrink: 0,
                        px: 1,
                        minWidth: 0,
                        "&:hover": { backgroundColor: "transparent", color: brandBlue },
                      }}
                    >
                      {contextOpen ? "Hide" : "Show"}
                    </Button>
                  )}
                </Stack>
                <Collapse in={contextOpen && includeContext}>
                  <Box sx={{
                    px: 2,
                    pb: 1.5,
                    pt: 1.5,
                    borderTop: `1px solid ${colors.neutral[300]}`,
                    fontFamily: "monospace",
                    fontSize: 12,
                    color: colors.neutral[700],
                    lineHeight: "18px",
                  }}>
                    <Box sx={{ mb: 0.5 }}><Box component="span" sx={{ color: colors.neutral[1000], fontWeight: 500 }}>URL:</Box> {context.page_url}</Box>
                    <Box sx={{ mb: 0.5 }}><Box component="span" sx={{ color: colors.neutral[1000], fontWeight: 500 }}>UA:</Box> {context.user_agent}</Box>
                    <Box sx={{ mb: 0.5 }}><Box component="span" sx={{ color: colors.neutral[1000], fontWeight: 500 }}>Viewport:</Box> {context.viewport}</Box>
                    <Box sx={{ mt: 1 }}>
                      <Box component="span" sx={{ color: colors.neutral[1000], fontWeight: 500 }}>Recent console errors:</Box> {(context.console_errors || []).length}
                    </Box>
                    {(context.console_errors || []).slice(0, 3).map((e, i) => (
                      <Box
                        key={i}
                        sx={{
                          mt: 0.75, p: 1,
                          bgcolor: "#fff",
                          borderRadius: "6px",
                          border: `1px solid ${colors.neutral[200]}`,
                          fontSize: 11,
                          wordBreak: "break-word",
                        }}
                      >
                        {String(e.message).slice(0, 200)}
                      </Box>
                    ))}
                    {(context.console_errors || []).length > 3 && (
                      <Typography sx={{ mt: 0.75, fontSize: 12, color: colors.neutral[600] }}>
                        + {(context.console_errors || []).length - 3} more
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </Box>

              {error && (
                <MuiAlert severity="error" onClose={() => setError(null)} sx={{ borderRadius: "8px" }}>
                  {error}
                </MuiAlert>
              )}
            </Stack>
          </Box>

          {/* Footer — pinned to drawer bottom */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{
              flexShrink: 0,
              px: 3,
              py: 2.5,
              borderTop: `1px solid ${colors.neutral[200]}`,
              backgroundColor: "#fff",
            }}
          >
            <CancelButton onClick={handleClose} disabled={submitting} sx={{ width: "auto", minWidth: 96, flex: "0 0 auto" }} />
            <Box sx={{ flex: 1 }} />
            <PrimaryButton
              onClick={handleSubmit}
              disabled={submitting || !canSubmit}
              startIcon={submitting ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
              sx={{ width: "auto", minWidth: 140 }}
            >
              {submitting ? "Submitting…" : "Submit report"}
            </PrimaryButton>
          </Stack>
        </Box>
      </Slide>
    </Modal>
  );
}
